// Mirrors the JSON shapes returned by internal/httpx (see docs/api.md).
// Kept as plain interfaces (no zod) — see docs/architecture.md for why this
// build has no runtime schema-validation library.

export interface CaseRecord {
  id: string;
  tenantId: string;
  caseNumber: string;
  merchantId: string;
  status: string;
  dataSource?: string;
  completionPercent: number;
  requestedAmount: number;
  assignedTo?: string;
  bankName: string;
  bankBranch?: string;
  createdAt: string;
  updatedAt: string;
  submittedAt?: string;
}

export interface MerchantProfile {
  caseId: string;
  storeName: string;
  ownerFirstName: string;
  ownerLastName: string;
  ownerNationalId: string;
  ownerMobile: string;
  businessCategory: string;
  businessStartDate?: string;
  postalCode: string;
  notes: string;
  fieldSources: Record<string, string>;
}

export interface AddressRecord {
  id?: string;
  caseId?: string;
  entityType: string;
  entityId?: string;
  line1: string;
  city: string;
  province: string;
  postalCode: string;
  propertyStatus: string;
}

export interface Guarantor {
  id: string;
  caseId: string;
  seq: number;
  firstName: string;
  lastName: string;
  nationalId: string;
  mobile: string;
  businessName: string;
  businessActivity: string;
  businessAddress: string;
}

export interface DocumentRecord {
  id: string;
  caseId: string;
  entityType: string;
  entityId?: string;
  documentType: string;
  required: boolean;
  status: string;
  currentVersionId?: string;
}

export interface DocumentVersion {
  id: string;
  documentId: string;
  versionNo: number;
  originalFilename: string;
  mimeType: string;
  sizeBytes: number;
  sha256: string;
  source: string;
  status: string;
  rejectionReason?: string;
  createdAt: string;
  uploadedByRole?: string;
}

export interface CompletenessSection {
  key: string;
  label: string;
  complete: boolean;
  detail?: string;
}

export interface Completeness {
  percent: number;
  sections: CompletenessSection[];
  missingCount: number;
  canSubmit: boolean;
}

export interface StatusHistoryEntry {
  fromStatus?: string;
  toStatus: string;
  note?: string;
  createdAt: string;
}

export interface CurrentCaseResponse {
  case: CaseRecord;
  profile: MerchantProfile;
  address: AddressRecord;
  guarantors: Guarantor[];
  documents: DocumentRecord[];
  completeness: Completeness;
}

export interface DashboardStats {
  total: number;
  newToday: number;
  incomplete: number;
  readyForReview: number;
  underReview: number;
  readyForBank: number;
  sentToBank: number;
}

export interface AdminCaseListItem extends CaseRecord {
  merchantZoodexId: string;
  merchantMobile: string;
  ownerName: string;
  storeName: string;
  assignedToName?: string;
}

export interface AdminCaseListResponse {
  items: AdminCaseListItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface AdminCaseDetail {
  case: CaseRecord;
  profile: MerchantProfile;
  applicantAddress: AddressRecord;
  guarantors: Guarantor[];
  guarantorAddresses: Record<string, AddressRecord>;
  documents: { document: DocumentRecord; versions: DocumentVersion[] }[];
  completeness: Completeness;
  history: StatusHistoryEntry[];
}

export const DOCUMENT_LABELS: Record<string, string> = {
  NATIONAL_ID_FRONT: "کارت ملی - روی کارت",
  NATIONAL_ID_BACK: "کارت ملی - پشت کارت",
  BUSINESS_LICENSE: "جواز کسب",
  BUSINESS_LICENSE_SUPPLEMENT: "نسخه تکمیلی جواز کسب",
  LEASE_AGREEMENT: "اجاره‌نامه",
  OWNERSHIP_DOCUMENT: "سند مالکیت",
  TAX_RETURN_YEAR1: "اظهارنامه سالانه (سال اول)",
  TAX_RETURN_YEAR2: "اظهارنامه سالانه (سال دوم)",
  QUARTERLY_TAX_RETURN: "آخرین اظهارنامه فصلی",
  SALES_FORECAST: "پیش‌بینی فروش",
  GUARANTOR_TAX_RETURN: "آخرین اظهارنامه مالیاتی",
};

export const STATUS_LABELS: Record<string, string> = {
  DRAFT: "پیش‌نویس",
  INCOMPLETE: "ناقص",
  SUBMITTED: "ثبت شده",
  DOCUMENT_REVIEW: "بررسی مدارک",
  CORRECTION_REQUIRED: "نیاز به اصلاح",
  CREDIT_REVIEW: "بررسی اعتباری",
  READY_FOR_BANK: "آماده ارسال به بانک",
  SENT_TO_BANK: "ارسال شده به بانک",
  BANK_REVIEW: "در حال بررسی بانک",
  APPROVED: "تأیید شده",
  REJECTED: "رد شده",
  CLOSED: "بسته شده",
};
